import { useState } from "react";
import { SubscribeGlobalState } from "../../common/hooks/global-state";
import { PageContainer } from "../components/layout/page";
import { HeroAttributeImage, HeroImage } from "../components/ui/file-contents";
import { SearchInput } from "../components/functional/inputs";
import { localize } from "../../common/services/localization";

export const Heroes: React.FC = () => {

    return (
        <PageContainer
            staticBg={true}
            containerClassName="flex flex-col justify-center items-center min-h-[350px] mt-[63px]"
            headerImgPath="/v2/images/bg/dragon.png"
            child={
                <div className="mx-[20px] 2xl:mx-[0px] 2xl:w-[1561px]">
                    <h2 className="mb-[60px] uppercase">{localize('heroes_title')}</h2>
                    <HeroesContainer />
                </div>
            }
        />
    );
}

const HeroesContainer: React.FC = () => {

    const heroes = SubscribeGlobalState('heroes')
    const newHero = SubscribeGlobalState('lastAddedHero')

    const [searchValue, setSearchValue] = useState<string | undefined>();
    const [showAttrFilter, setShowAttrFilter] = useState<{ attr: HeroAttribute, show: boolean }[]>([
        { attr: 'Strength', show: false },
        { attr: 'Agility', show: false },
        { attr: 'Intellect', show: false },
        { attr: 'All', show: false },
    ]);

    function updateSearchValue(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        const condition = value.trim();
        setSearchValue(condition.length > 0 ? condition.toLowerCase() : undefined);
    }

    function updateAttrFilter(attr: HeroAttribute) {
        setShowAttrFilter(prev =>
            prev.map(item =>
                item.attr === attr
                    ? { ...item, show: !item.show }
                    : item
            )
        );
    }

    const isAllFalse = !showAttrFilter.find(f => f.show);

    const values = heroes?.filter(hero => {
        const matchesSearch = !searchValue || hero.urlName.includes(searchValue);
        const matchesAttr = isAllFalse || showAttrFilter.find(f => f.attr === hero.attribute && f.show);

        return matchesSearch && matchesAttr;
    }) ?? [];


    return <div>
        <div className="mb-[30px] lg:mb-[50px] flex flex-col md:flex-row items-center justify-center gap-[18px] md:gap-[40px] lg:gap-[80px]">
            <SearchInput
                className="w-[350px] md:w-[500px] lg:w-[680px]"
                placeholderLocal="search_hero_placeholder"
                onChange={updateSearchValue} />
            <div className="flex items-center gap-[18px]">
                <div className="flex items-center gap-[6px]">
                    {
                        showAttrFilter.map(filter => <div>
                            <img className={`w-[32px] h-[32px] cursor-pointer ${filter.show ? 'brightness-[100%]' : 'brightness-[40%]'} `}
                                onClick={() => updateAttrFilter(filter.attr)}
                                src={`/images/${filter.attr}-active.png`}
                            />
                        </div>)
                    }
                </div>
            </div>
        </div>
        <div className="flex flex-col gap-y-[48px]">
            {showAttrFilter
                .filter(({ attr, show }) => {
                    return isAllFalse || show;
                })
                .map(({ attr }) => {
                    const filtered = values.filter(hero => hero.attribute === attr);

                    return filtered.length > 0 ? (
                        <HeroesAttrBlock key={attr} newHero={newHero!} heroes={filtered} attr={attr} />
                    ) : null;
                })}
        </div>

    </div>
}

const HeroesAttrBlock: React.FC<{
    heroes: Hero[]
    newHero: Hero
    attr: HeroAttribute
}> = ({ heroes, attr, newHero }) => {

    return <div>
        <div>
            <div className="flex items-center gap-[12px]">
                <HeroAttributeImage attr={attr} className="w-[32px]" />
                <div className="uppercase text-[18px] font-semibold">
                    {localize(attr)}
                </div>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 w-auto gap-[12px] mt-[18px]">
                {heroes.map(hero => <HeroCard
                    key={hero.name}
                    isNewHero={hero.name == newHero.name}
                    hero={hero} />)}
            </div>
        </div>
    </div>
}

const HeroCard: React.FC<{
    hero: Hero
    isNewHero: boolean
}> = ({ hero, isNewHero }) => {
    return <a
        href={`/heroes/${hero.urlName}`}
        className={`relative shadow-dark-sm overflow-hidden transition-transform duration-300
        hover:z-[10] hover:brightness-[110%] hover:scale-150 group`}>
        <HeroImage className={`w-full h-full object-cover`}
            heroName={hero.name}
            size="base"
        />
        {
            isNewHero && <div className="absolute top-0 right-0 bg-green text-white text-right px-[5px] py-[3px] 
            font-['Roboto'] font-semibold text-[10px] md:text-[12px] lg:text-[14px]  xl:text-[18px] shadow-text-dark w-auto">
                {`NEW`}
            </div>
        }
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/100 to-transparent 
                opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="mx-2 w-full text-left text-[12px] font-semibold absolute bottom-2 left-1/2
         transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {`${hero.userFriendlyName}`}
        </div>
    </a>

}

